import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js'

import { PAD, niceTicks } from './charts.axis'
import { useWidth } from './use-width'
import Css from './charts.module.scss'

export type ComboSeries = {
    name: string
    values: number[]
    kind: 'area' | 'line' | 'bar'
    color: string
}

/**
 * Barras e curvas no MESMO grafico, sobre um eixo X de categorias.
 *
 * E o que a Adocao e Engajamento pede em toda secao: uma medida em barra (quantos
 * treinos) com outra em curva por cima (quantas pessoas), no mesmo periodo. Duas
 * naturezas de numero que so fazem sentido lidas juntas.
 *
 * O eixo X aqui e CATEGORICO, e nao numerico como no `SeriesChart`: os pontos ja
 * vem agrupados por dia ou por mes pelo servidor, e o rotulo de cada um e texto.
 */
export function ComboChart(props: {
    labels: string[]
    series: ComboSeries[]
    height?: number
    format?: (value: number) => string
}): JSX.Element {
    const [width, observe] = useWidth()
    const [hover, setHover] = createSignal<number | undefined>()

    const height = () => props.height ?? 300
    const format = (value: number) => (props.format ?? ((v: number) => String(Math.round(v * 10) / 10)))(value)

    const plot = () => ({
        width: Math.max(0, width() - PAD.left - PAD.right),
        height: Math.max(0, height() - PAD.top - PAD.bottom)
    })

    const max = createMemo(() => {
        const values = props.series.flatMap((serie) => serie.values)
        return values.length > 0 ? Math.max(1, ...values) : 1
    })

    const ticks = createMemo(() => niceTicks(0, max()))

    /** O centro da fatia de cada categoria. */
    const slice = () => plot().width / Math.max(1, props.labels.length)
    const centerX = (index: number) => PAD.left + slice() * (index + 0.5)
    const scaleY = (value: number) => PAD.top + plot().height - (value / (ticks().at(-1) ?? max())) * plot().height

    const bars = () => props.series.filter((serie) => serie.kind === 'bar')

    const onMove = (event: PointerEvent) => {
        const rect = (event.currentTarget as SVGElement).getBoundingClientRect()
        const index = Math.floor((event.clientX - rect.left - PAD.left) / slice())
        setHover(index >= 0 && index < props.labels.length ? index : undefined)
    }

    /** No maximo seis rotulos no eixo: mais do que isso vira borrao. */
    const labelTicks = createMemo(() => {
        const step = Math.max(1, Math.ceil(props.labels.length / 6))
        return props.labels.map((label, index) => ({ label, index })).filter(({ index }) => index % step === 0)
    })

    let host!: HTMLDivElement
    createEffect(() => observe(host))

    return (
        <div class={Css.lines}>
            <div ref={host} class={Css.frame} style={{ height: `${height()}px` }}>
                <Show
                    when={props.labels.length > 0 && width() > 0}
                    fallback={<p class={Css.empty}>Sem dado no período</p>}
                >
                    <svg
                        width={width()}
                        height={height()}
                        onPointerMove={onMove}
                        onPointerLeave={() => setHover(undefined)}
                    >
                        <For each={ticks()}>
                            {(tick) => (
                                <>
                                    <line
                                        x1={PAD.left}
                                        x2={PAD.left + plot().width}
                                        y1={scaleY(tick)}
                                        y2={scaleY(tick)}
                                        stroke="var(--line-2)"
                                        stroke-dasharray="3 3"
                                    />
                                    <text
                                        x={PAD.left - 10}
                                        y={scaleY(tick)}
                                        text-anchor="end"
                                        dominant-baseline="middle"
                                        class={Css.axisNumber}
                                    >
                                        {format(tick)}
                                    </text>
                                </>
                            )}
                        </For>

                        <For each={labelTicks()}>
                            {({ label, index }) => (
                                <text x={centerX(index)} y={height() - 8} text-anchor="middle" class={Css.axis}>
                                    {label}
                                </text>
                            )}
                        </For>

                        {/* A coluna sob o ponteiro, atras de tudo. */}
                        <Show when={hover() !== undefined}>
                            <rect
                                x={PAD.left + slice() * hover()!}
                                y={PAD.top}
                                width={slice()}
                                height={plot().height}
                                fill="var(--line-2)"
                                opacity="0.6"
                            />
                        </Show>

                        <For each={props.series}>
                            {(serie, serieIndex) => (
                                <Show
                                    when={serie.kind === 'bar'}
                                    fallback={
                                        <ComboCurve
                                            serie={serie}
                                            centerX={centerX}
                                            scaleY={scaleY}
                                            floor={PAD.top + plot().height}
                                        />
                                    }
                                >
                                    {/* Barras de series diferentes ficam LADO a lado. */}
                                    <For each={serie.values}>
                                        {(value, index) => {
                                            const count = bars().length
                                            const position = bars().indexOf(serie)
                                            const band = slice() * 0.62
                                            const barWidth = band / count
                                            const left = centerX(index()) - band / 2 + barWidth * position

                                            return (
                                                <rect
                                                    x={left}
                                                    y={scaleY(value)}
                                                    width={Math.max(1, barWidth - 1.5)}
                                                    height={Math.max(0, PAD.top + plot().height - scaleY(value))}
                                                    rx="3"
                                                    fill={serie.color}
                                                    opacity={serieIndex() === 0 ? 0.9 : 0.75}
                                                />
                                            )
                                        }}
                                    </For>
                                </Show>
                            )}
                        </For>
                    </svg>

                    <Show when={hover() !== undefined}>
                        <div
                            class={`${Css.tooltip} ${Css.beside}`}
                            style={{
                                left: `${Math.min(Math.max(centerX(hover()!), 90), width() - 90)}px`,
                                top: '8px'
                            }}
                        >
                            <p class={Css.tooltipTitle}>{props.labels[hover()!]}</p>
                            <For each={props.series}>
                                {(serie) => (
                                    <p class={Css.tooltipRow}>
                                        <span class={Css.tooltipDot} style={{ 'background-color': serie.color }} />
                                        {serie.name}
                                        <span class={Css.tooltipNumber}>{format(serie.values[hover()!] ?? 0)}</span>
                                    </p>
                                )}
                            </For>
                        </div>
                    </Show>
                </Show>
            </div>

            <div class={Css.legend}>
                <For each={props.series}>
                    {(serie) => (
                        <span class={Css.legendItem}>
                            <span class={Css.legendSwatch} style={{ 'background-color': serie.color }} />
                            <span class={Css.legendName}>{serie.name}</span>
                        </span>
                    )}
                </For>
            </div>
        </div>
    )
}

function ComboCurve(props: {
    serie: ComboSeries
    centerX: (index: number) => number
    scaleY: (value: number) => number
    floor: number
}): JSX.Element {
    const line = () =>
        props.serie.values
            .map(
                (value, index) =>
                    `${index === 0 ? 'M' : 'L'} ${props.centerX(index).toFixed(2)} ${props.scaleY(value).toFixed(2)}`
            )
            .join(' ')

    return (
        <>
            <Show when={props.serie.kind === 'area' && props.serie.values.length > 1}>
                <path
                    d={`${line()} L ${props.centerX(props.serie.values.length - 1).toFixed(2)} ${props.floor.toFixed(2)} L ${props.centerX(0).toFixed(2)} ${props.floor.toFixed(2)} Z`}
                    fill={props.serie.color}
                    opacity="0.14"
                />
            </Show>
            <path
                d={line()}
                fill="none"
                stroke={props.serie.color}
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </>
    )
}
